import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add as AddIcon, DeleteOutline as DeleteIcon } from '@mui/icons-material';
import { ModelGroup, ModelGroups, TestsApi } from '../../services/testsApi';
import { ModelChecklist, forgetModelGroups } from './common';
import { errorText } from './dialogs';

type Draft = Omit<ModelGroup, 'id'> & { id?: string };

const defaultFor = (state: ModelGroups, id: string): string | null => {
  const kinds = Object.entries(state.kind_groups)
    .filter(([, g]) => g === id)
    .map(([k]) => k);
  if (id === state.fallback_group) return kinds.length ? `${kinds.join(', ')} and every other route` : 'every other route';
  return kinds.length ? `${kinds.join(', ')} routes` : null;
};

/** Admin: the model groups platform routes test on (and everyone gets as quick picks). */
export const ModelGroupsDialog: React.FC<{
  open: boolean;
  api: TestsApi;
  onClose: () => void;
  onSaved: (groups: ModelGroups) => void;
}> = ({ open, api, onClose, onSaved }) => {
  const [state, setState] = useState<ModelGroups | null>(null);
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setState(null);
    api
      .modelGroups()
      .then(s => {
        setState(s);
        setDrafts(s.groups);
      })
      .catch(e => setError(errorText(e)));
  }, [api, open]);

  const update = (i: number, patch: Partial<Draft>) => setDrafts(d => d.map((g, j) => (j === i ? { ...g, ...patch } : g)));
  const builtIn = (g: Draft) => !!g.id && !!state?.built_in.includes(g.id);
  const invalid = drafts.some(g => !g.name.trim());

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const saved = await api.saveModelGroups(drafts.map(g => ({ ...g, name: g.name.trim() })));
      forgetModelGroups();
      onSaved(saved);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Model groups</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Each platform route tests on its group when you pick "Each route's model group". Routes use their kind's group unless you set
            one in the table. Everyone also sees these groups as quick picks.
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          {state && !state.saved && (
            <Alert severity="info">These are suggestions from the model catalog (each provider's best model per tier). Save to keep them as they are.</Alert>
          )}
          {!state && !error && <CircularProgress size={24} />}
          {state &&
            drafts.map((g, i) => {
              const usedBy = g.id ? defaultFor(state, g.id) : null;
              const suggested = g.id ? state.suggested[g.id] : undefined;
              return (
                <Box key={g.id || `new-${i}`} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1.5 }}>
                    <TextField size="small" label="Name" value={g.name} onChange={e => update(i, { name: e.target.value })} sx={{ width: 200 }} />
                    <TextField
                      size="small"
                      label="What it's for"
                      value={g.description}
                      onChange={e => update(i, { description: e.target.value })}
                      fullWidth
                    />
                    {builtIn(g) ? (
                      suggested && (
                        <Button size="small" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }} onClick={() => update(i, { models: suggested })}>
                          Reset to suggested
                        </Button>
                      )
                    ) : (
                      <Tooltip title="Delete group">
                        <IconButton size="small" onClick={() => setDrafts(d => d.filter((_, j) => j !== i))}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                  {usedBy && (
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                      Used by {usedBy} unless a route has its own group.
                    </Typography>
                  )}
                  <ModelChecklist value={g.models} onChange={models => update(i, { models })} context={{}} allowDefault={false} />
                </Box>
              );
            })}
          {state && (
            <Box>
              <Button startIcon={<AddIcon />} onClick={() => setDrafts(d => [...d, { name: '', description: '', models: [] }])}>
                Add group
              </Button>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={!state || saving || invalid}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
